import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'lucide-react';
import {
  Calendar,
  cn,
  Label,
  Popover,
  PopoverAnchor,
  PopoverContent,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@scio3/ui';
import { isEnterKey } from '@/lib/keyboard';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const HOURS_24 = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

/** dayjs uppercase H → 24-hour clock; lowercase h (or no H/h) → 12-hour. */
function is24HourPattern(pattern: string): boolean {
  return /H/.test(pattern);
}

function to12Hour(h24: number): { h12: number; period: 'AM' | 'PM' } {
  return { h12: h24 % 12 || 12, period: h24 >= 12 ? 'PM' : 'AM' };
}

function to24Hour(h12: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

/** Read Y/M/D in the chosen frame (tz takes precedence over utc). */
function ymd(d: Date, tz: string | undefined, useUtc: boolean) {
  if (tz) {
    const m = dayjs(d).tz(tz);
    return { y: m.year(), mo: m.month() + 1, da: m.date() };
  }
  if (useUtc) return { y: d.getUTCFullYear(), mo: d.getUTCMonth() + 1, da: d.getUTCDate() };
  return { y: d.getFullYear(), mo: d.getMonth() + 1, da: d.getDate() };
}

/** Read H/M in the chosen frame. */
function hm(d: Date, tz: string | undefined, useUtc: boolean) {
  if (tz) {
    const m = dayjs(d).tz(tz);
    return { h: m.hour(), mi: m.minute() };
  }
  if (useUtc) return { h: d.getUTCHours(), mi: d.getUTCMinutes() };
  return { h: d.getHours(), mi: d.getMinutes() };
}

function formatDatePart(
  d: Date,
  tz: string | undefined,
  useUtc: boolean,
  dateFormat: string,
): string {
  if (tz) return dayjs(d).tz(tz).format(dateFormat);
  if (useUtc) return dayjs.utc(d).format(dateFormat);
  return dayjs(d).format(dateFormat);
}

function formatTimePart(d: Date, tz: string | undefined, useUtc: boolean, is24: boolean): string {
  const { h, mi } = hm(d, tz, useUtc);
  if (is24) return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
  const { h12, period } = to12Hour(h);
  return `${h12}:${String(mi).padStart(2, '0')} ${period}`;
}

type DatePart = { year: number; month: number; day: number };
type TimePart = { hours24: number; minutes: number };

/** Find an MM/DD/YYYY style date anywhere in the input.
 *  Year is lenient — 1, 2, 3, or 4 digits. Anything < 100 is treated as 2000 + YY
 *  (so "9-3-2" → 2002, "9-3-24" → 2024). */
function findDate(input: string): DatePart | null {
  const match = input.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{1,4})/);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  let year = Number(match[3]);
  if (year < 100) year = 2000 + year;
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2999) return null;
  const test = new Date(Date.UTC(year, month - 1, day));
  if (
    test.getUTCFullYear() !== year ||
    test.getUTCMonth() !== month - 1 ||
    test.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

/** Find an H:MM (optional AM/PM) time anywhere in the input. */
function findTime(input: string): TimePart | null {
  const match = input.match(/(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/);
  if (!match) return null;
  const hRaw = Number(match[1]);
  const minutes = Number(match[2]);
  if (minutes < 0 || minutes > 59) return null;
  const period = match[3]?.toUpperCase() as 'AM' | 'PM' | undefined;
  if (period) {
    if (hRaw < 1 || hRaw > 12) return null;
    return { hours24: to24Hour(hRaw, period), minutes };
  }
  if (hRaw < 0 || hRaw > 23) return null;
  return { hours24: hRaw, minutes };
}

/** Try to extract whichever of date/time the user typed or pasted. */
function parseAny(input: string): { date?: DatePart; time?: TimePart } {
  const trimmed = input.trim();
  if (!trimmed) return {};
  return {
    date: findDate(trimmed) ?? undefined,
    time: findTime(trimmed) ?? undefined,
  };
}

export interface DateTimePickerProps {
  value: string;
  onChange: (iso: string) => void;
  label: string;
  error?: string;
  invalid?: boolean;
  placeholder?: string;
  timeLabel?: string;
  utc?: boolean;
  /** IANA timezone (e.g. 'America/New_York'). When set, takes precedence over `utc` —
   *  the picker reads/writes wall-clock time in this zone, while storage stays ISO UTC. */
  tz?: string;
  /** dayjs date pattern for the input display. Default 'MM/DD/YYYY'. */
  dateFormat?: string;
  /** dayjs time pattern — uppercase H switches the picker to 24-hour mode. Default 'hh:mm A'. */
  timeFormat?: string;
  disabled?: boolean;
  /** Optional stable identifier for E2E tests. When set, the calendar
   *  toggle button receives `data-testid={testId}-calendar-btn`. */
  testId?: string;
}

export function DateTimePicker({
  value,
  onChange,
  label,
  error,
  invalid,
  placeholder,
  timeLabel = 'Time',
  utc: useUtc = true,
  tz,
  dateFormat = 'MM/DD/YYYY',
  timeFormat = 'hh:mm A',
  disabled,
  testId,
}: DateTimePickerProps) {
  const { t } = useTranslation();
  const is24Hour = is24HourPattern(timeFormat);
  const date = value ? new Date(value) : undefined;
  const isInvalid = !!error || !!invalid;

  const [dateText, setDateText] = useState(
    date ? formatDatePart(date, tz, useUtc, dateFormat) : '',
  );
  const [timeText, setTimeText] = useState(date ? formatTimePart(date, tz, useUtc, is24Hour) : '');
  const [open, setOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const lastSyncedValueRef = useRef(value);
  useEffect(() => {
    if (value !== lastSyncedValueRef.current) {
      lastSyncedValueRef.current = value;
      if (date) {
        setDateText(formatDatePart(date, tz, useUtc, dateFormat));
        setTimeText(formatTimePart(date, tz, useUtc, is24Hour));
      } else {
        setDateText('');
        setTimeText('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // react-day-picker compares dates using local timezone (date-fns isSameDay → startOfDay).
  // Convert to a local-equivalent date so the calendar highlights the correct day in tz/UTC mode.
  const calendarDate = date
    ? tz
      ? (() => {
          const m = dayjs(date).tz(tz);
          return new Date(m.year(), m.month(), m.date());
        })()
      : useUtc
        ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
        : date
    : undefined;

  /** Commit any combination of date/time parts, preserving whatever wasn't provided. */
  const commitParts = (parts: { date?: DatePart; time?: TimePart }) => {
    const base = date ?? new Date();
    const cur = ymd(base, tz, useUtc);
    const curT = hm(base, tz, useUtc);
    const y = parts.date?.year ?? cur.y;
    const mo = parts.date ? parts.date.month : cur.mo;
    const da = parts.date?.day ?? cur.da;
    const h = parts.time?.hours24 ?? curT.h;
    const mi = parts.time?.minutes ?? curT.mi;

    let nextIso: string;
    if (tz) {
      const pad = (n: number) => String(n).padStart(2, '0');
      const wallClock = `${y}-${pad(mo)}-${pad(da)} ${pad(h)}:${pad(mi)}`;
      nextIso = dayjs.tz(wallClock, 'YYYY-MM-DD HH:mm', tz).utc().toISOString();
    } else {
      const next = useUtc
        ? new Date(Date.UTC(y, mo - 1, da, h, mi, 0, 0))
        : new Date(y, mo - 1, da, h, mi, 0, 0);
      nextIso = next.toISOString();
    }
    onChange(nextIso);
  };

  const handleCalendarSelect = (day: Date | undefined) => {
    if (!day) return;
    commitParts({
      date: { year: day.getFullYear(), month: day.getMonth() + 1, day: day.getDate() },
    });
  };

  const handleDateBlur = () => {
    const parts = parseAny(dateText);
    if (parts.date || parts.time) commitParts(parts);
    else setDateText(date ? formatDatePart(date, tz, useUtc, dateFormat) : '');
  };

  const handleTimeBlur = () => {
    const parts = parseAny(timeText);
    if (parts.date || parts.time) commitParts(parts);
    else setTimeText(date ? formatTimePart(date, tz, useUtc, is24Hour) : '');
  };

  const openIfEnabled = () => {
    if (!disabled) setOpen(true);
  };

  const curHM = date ? hm(date, tz, useUtc) : { h: 0, mi: 0 };
  const h24 = curHM.h;
  const mins = curHM.mi;
  const { h12, period } = to12Hour(h24);

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div
            ref={containerRef}
            aria-invalid={isInvalid || undefined}
            className={cn(
              'flex h-9 w-full items-stretch rounded-md border border-input bg-transparent text-sm transition-colors',
              !disabled &&
                'hover:border-muted-foreground/70 focus-within:ring-1 focus-within:ring-ring',
              isInvalid && 'border-destructive focus-within:ring-destructive',
              isInvalid && !disabled && 'hover:border-destructive',
              disabled && 'cursor-not-allowed bg-muted text-muted-foreground',
            )}
          >
            <div className="flex min-w-[8rem] shrink-0 grow basis-1/2 items-center">
              <button
                type="button"
                disabled={disabled}
                aria-label={`${label} — open calendar`}
                data-testid={testId ? `${testId}-calendar-btn` : undefined}
                onClick={openIfEnabled}
                className={cn(
                  'flex shrink-0 items-center justify-center pl-3 pr-2 text-muted-foreground transition-colors',
                  !disabled && 'cursor-pointer hover:text-foreground',
                  disabled && 'cursor-not-allowed',
                )}
              >
                <CalendarIcon className="h-4 w-4" />
              </button>
              <input
                type="text"
                size={10}
                value={dateText}
                onChange={(e) => setDateText(e.target.value)}
                onBlur={handleDateBlur}
                onFocus={openIfEnabled}
                onClick={openIfEnabled}
                onKeyDown={(e) => {
                  if (isEnterKey(e)) {
                    e.preventDefault();
                    handleDateBlur();
                    setOpen(false);
                    e.currentTarget.blur();
                  }
                }}
                disabled={disabled}
                placeholder={placeholder ?? dateFormat}
                aria-label={`${label} date`}
                className={cn(
                  'min-w-0 flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground',
                  disabled && 'cursor-not-allowed',
                )}
              />
            </div>
            <div className="my-1.5 w-px shrink-0 bg-input" />
            <input
              type="text"
              size={7}
              value={timeText}
              onChange={(e) => setTimeText(e.target.value)}
              onBlur={handleTimeBlur}
              onFocus={openIfEnabled}
              onClick={openIfEnabled}
              onKeyDown={(e) => {
                if (isEnterKey(e)) {
                  e.preventDefault();
                  handleTimeBlur();
                  setOpen(false);
                  e.currentTarget.blur();
                }
              }}
              disabled={disabled}
              placeholder={is24Hour ? '09:00' : '9:00 AM'}
              aria-label={`${label} time`}
              className={cn(
                'min-w-0 grow basis-1/2 bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground',
                disabled && 'cursor-not-allowed',
              )}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          collisionPadding={8}
          // Don't steal focus from the typing input when the popover opens
          onOpenAutoFocus={(e) => e.preventDefault()}
          // Radix would treat clicks on the input as "outside" (the trigger is the container
          // but we use PopoverAnchor, not PopoverTrigger). Keep the popover open whenever
          // the click is inside our container.
          onInteractOutside={(e) => {
            if (containerRef.current?.contains(e.target as Node)) e.preventDefault();
          }}
        >
          {/* Inner scroll container. The parent DialogScrollBody (OverlayScrollbars)
           *  registers a document-level wheel listener that calls preventDefault when
           *  the cursor isn't over its container — that kills native scroll inside our
           *  Radix-portaled popup. Stopping propagation here keeps the wheel event
           *  local so the browser scrolls this div normally. */}
          <div
            className="overflow-y-auto"
            style={{ maxHeight: 'var(--radix-popover-content-available-height)' }}
            onWheel={(e) => e.stopPropagation()}
          >
            <Calendar
              mode="single"
              selected={calendarDate}
              defaultMonth={calendarDate}
              onSelect={handleCalendarSelect}
            />
            <div className="border-t p-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{timeLabel}:</span>
                <Select
                  value={String(is24Hour ? h24 : h12)}
                  onValueChange={(v) =>
                    commitParts({
                      time: {
                        hours24: is24Hour ? Number(v) : to24Hour(Number(v), period),
                        minutes: mins,
                      },
                    })
                  }
                >
                  <SelectTrigger className="h-8 w-[60px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <ScrollArea className="h-48">
                      {(is24Hour ? HOURS_24 : HOURS_12).map((h) => (
                        <SelectItem key={h} value={String(h)}>
                          {is24Hour ? String(h).padStart(2, '0') : h}
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">:</span>
                <Select
                  value={String(mins)}
                  onValueChange={(v) => commitParts({ time: { hours24: h24, minutes: Number(v) } })}
                >
                  <SelectTrigger className="h-8 w-[60px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <ScrollArea className="h-48">
                      {MINUTES.map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {String(m).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
                {!is24Hour && (
                  <Select
                    value={period}
                    onValueChange={(v) =>
                      commitParts({
                        time: { hours24: to24Hour(h12, v as 'AM' | 'PM'), minutes: mins },
                      })
                    }
                  >
                    <SelectTrigger className="h-8 w-[65px] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">{t('AM')}</SelectItem>
                      <SelectItem value="PM">{t('PM')}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
